import { initializeApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from 'firebase/app-check';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase app first
const app = initializeApp(firebaseConfig);

// Initialize services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'us-central1');
export const storage = getStorage(app);

// Enable App Check debug token for local development
// This must be set BEFORE initializeAppCheck() is called
if (import.meta.env.DEV) {
  (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

// Initialize App Check with ReCaptcha Enterprise
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

// Hard failure in production — App Check is not optional
if (import.meta.env.PROD && !recaptchaSiteKey) {
  throw new Error(
    'FATAL: VITE_RECAPTCHA_SITE_KEY is missing. ' +
    'App Check cannot be initialized. Refusing to start in production without App Check.'
  );
}

export let appCheck: AppCheck | null = null;

if (recaptchaSiteKey) {
  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
} else if (import.meta.env.DEV) {
  // Dev environment without key — App Check skipped (OK for local dev with emulators)
  console.warn('App Check: VITE_RECAPTCHA_SITE_KEY not set, skipping initialization (dev mode)');
}

// Development mode: connect to emulators
if (import.meta.env.DEV) {
  console.log('Connecting to Firebase emulators...');

  // Auth emulator on port 9099
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });

  // Firestore emulator on port 8080
  connectFirestoreEmulator(db, '127.0.0.1', 8080);

  // Functions emulator on port 5001
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);

  // Storage emulator on port 9199
  connectStorageEmulator(storage, '127.0.0.1', 9199);

  console.log('Connected to Firebase emulators');
}

export default app;
